import {
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  RadioGroup,
} from '@material-ui/core';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import { makeStyles } from '@material-ui/core/styles';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';
import { color } from '../../app/theme';
import useDebounce from '../../hooks/useDebounce';
import AutoCompletedTextField from './AutoCompletedTextField';
import Button from './Button';
import DatePicker from './DatePicker';
import Radio from './Radio';
import TextField from './TextField';

const useStyles = makeStyles(
  theme => ({
    item: {
      display: 'flex',
      minHeight: 44,
      alignItems: 'center',
      '&+&': {
        marginTop: 10,
      },
    },
    radioContainer: {
      padding: '16px 16px 16px 25px',
    },
    radioitem: {
      marginRight: 0,
    },
    subheading: {
      ...theme.typography.subheading,
      fontWeight: 500,
      marginBottom: 35,
    },
    title: {
      ...theme.typography.subheading,
    },
    datepicker: {
      width: 144,
      margin: '0px 8px',
    },
    tagInput: {
      width: 418,
      margin: '0px 8px',
    },
    numInput: {
      width: 73,
      margin: '0px 8px',
    },
    finishBlock: {
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'center',
    },
    row: {
      display: 'flex',
      fontFamily: 'PingFang TC',
      fontWeight: 400,
      fontSize: 15,
      lineHeight: '21px',
      color: color.$11,
      backgroundColor: color.$19,
      borderRadius: 22,
      padding: '10px 28px 10px 16px',
      cursor: 'pointer',
      alignItems: 'center',
      flexWrap: 'wrap',
      '& > img': {
        width: 16,
        height: 16,
        position: 'absolute',
        right: '8px',
      },
    },
    leftText: {
      color: color.$12,
    },
    leftRightText: {
      color: color.$12,
      marginLeft: 4,
      marginRight: 4,
    },
    root: { zIndex: '9999 !important' },
    paper: {
      width: 765,
      maxWidth: 'unset',
      borderRadius: 12,
      boxShadow:
        '0 30px 66px 0 rgba(0, 0, 0, 0.09), 0 8px 58px 0 rgba(0, 0, 0, 0.05)',
    },
    dialogTitle: {
      paddingTop: 24,
      paddingBottom: 24,
      textAlign: 'center',
      backgroundColor: color.$7,
      '& > h2': {
        fontWeight: 400,
        fontSize: 20,
        lineHeight: 1,
        color: color.$3,
      },
    },
    content: {
      padding: '0',
      fontSize: 15,
      color: color.$3,
      backgroundColor: color.$26,
    },
  }),
  {
    name: 'TagConditionPopup',
  },
);

const TagConditionPopup = ({
  onAddConditionValue,
  conditionValue,
  disabled,
  setOpen,
  findTag,
  tagCandidates,
  disabledDate,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { orgId } = useParams();
  const classes = useStyles();

  const TIME_ENUM = {
    SET_TIME: 'setTime',
    NO_SET_TIME: 'noSetTime',
  };

  const TYPE_ENUM = {
    INCLUDE: 'include',
    EXCLUDE: 'exclude',
    ANY_GREATER: 'anyGreater',
    NONE: 'none',
  };

  const [time, setTime] = useState(
    conditionValue?.startDate ? TIME_ENUM.SET_TIME : TIME_ENUM.NO_SET_TIME,
  );
  const [type, setType] = useState(conditionValue?.type || TYPE_ENUM.NONE);

  const [startDate, setStartDate] = useState(conditionValue?.startDate);

  const [endDate, setEndDate] = useState(conditionValue?.endDate);
  const [includeCount, setIncludeCount] = useState(
    conditionValue?.type === TYPE_ENUM.INCLUDE ? conditionValue?.count : '',
  );
  const [someCount, setSomeCount] = useState(
    conditionValue?.type === TYPE_ENUM.ANY_GREATER ? conditionValue?.count : '',
  );

  const [includeTags, setIncludeTags] = useState(
    conditionValue?.type === TYPE_ENUM.INCLUDE ? conditionValue?.tags : [],
  );
  const [excludeTags, setExcludeTags] = useState(
    conditionValue?.type === TYPE_ENUM.EXCLUDE ? conditionValue?.tags : [],
  );

  const defaultStartDate = moment().add(-7, 'days').format('YYYYMMDD');
  const defaultEndDate = moment().format('YYYYMMDD');

  useEffect(() => {
    setTime(
      conditionValue?.startDate ? TIME_ENUM.SET_TIME : TIME_ENUM.NO_SET_TIME,
    );
    setType(conditionValue?.type || TYPE_ENUM.NONE);
    setStartDate(conditionValue?.startDate);
    setEndDate(conditionValue?.endDate);
    setIncludeCount(
      conditionValue?.type === TYPE_ENUM.INCLUDE ? conditionValue?.count : '',
    );
    setSomeCount(
      conditionValue?.type === TYPE_ENUM.ANY_GREATER
        ? conditionValue?.count
        : '',
    );

    setIncludeTags(
      conditionValue?.type === TYPE_ENUM.INCLUDE ? conditionValue?.tags : [],
    );

    setExcludeTags(
      conditionValue?.type === TYPE_ENUM.EXCLUDE ? conditionValue?.tags : [],
    );
  }, [conditionValue]);

  useEffect(() => {
    dispatch(findTag(undefined, orgId, undefined, { limit: 20 }));
  }, []);

  const handleChangeTime = value => {
    setTime(value);

    if (value === TIME_ENUM.NO_SET_TIME) {
      setStartDate();
      setEndDate();
      onAddConditionValue({
        ...conditionValue,
        startDate: undefined,
        endDate: undefined,
      });
    } else {
      setStartDate(defaultStartDate);
      setEndDate(defaultEndDate);
      onAddConditionValue({
        ...conditionValue,
        startDate: defaultStartDate,
        endDate: defaultEndDate,
      });
    }
  };

  const handleChangeType = value => {
    let count;
    if (value === TYPE_ENUM.INCLUDE) {
      setIncludeCount(1);
      count = 1;
    } else {
      setIncludeCount('');
    }

    if (value === TYPE_ENUM.ANY_GREATER) {
      setSomeCount(1);
      count = 1;
    } else {
      setSomeCount('');
    }

    setIncludeTags([]);
    setExcludeTags([]);

    onAddConditionValue({
      ...conditionValue,
      type: value,
      count,
      tags: [],
    });
  };

  const handleChangeStartDate = value => {
    onAddConditionValue({
      ...conditionValue,
      startDate: moment(value).format('YYYYMMDD'),
    });
  };

  const handleChangeEndDate = value => {
    onAddConditionValue({
      ...conditionValue,
      endDate: moment(value).format('YYYYMMDD'),
    });
  };

  const handleChangeIncludeTags = tags => {
    onAddConditionValue({
      ...conditionValue,
      type: TYPE_ENUM.INCLUDE,
      tags,
      count: conditionValue?.count || 1,
    });
  };

  const handleChangeExcludeTags = tags => {
    onAddConditionValue({
      ...conditionValue,
      type: TYPE_ENUM.EXCLUDE,
      tags,
    });
  };

  const handleChangeIncludeCount = value => {
    if (type !== TYPE_ENUM.INCLUDE) {
      onAddConditionValue({
        ...conditionValue,
        type: TYPE_ENUM.INCLUDE,
        tags: [],
        count: value,
      });
    } else {
      onAddConditionValue({
        ...conditionValue,
        type: TYPE_ENUM.INCLUDE,
        count: value,
      });
    }
  };

  const handleChangeSomeCount = value => {
    onAddConditionValue({
      ...conditionValue,
      type: TYPE_ENUM.ANY_GREATER,
      count: value,
      tags: [],
    });
  };

  const dbsHandleTagChange = useDebounce(value => {
    dispatch(findTag(value, orgId, undefined, { limit: 20 }));
  });

  function getContent() {
    return (
      <ClickAwayListener onClickAway={() => setOpen(false)}>
        <div>
          {!disabledDate && (
            <>
              <div className={classes.radioContainer}>
                <RadioGroup
                  value={time}
                  onChange={e => {
                    setTime(e.target.value);
                    handleChangeTime(e.target.value);
                  }}
                >
                  <div className={classes.item}>
                    <FormControlLabel
                      className={classes.radioitem}
                      value={TIME_ENUM.NO_SET_TIME}
                      control={<Radio />}
                      label={
                        <span className={classes.title}>
                          {t('tagCondition.noSetTime')}
                        </span>
                      }
                    />
                  </div>
                  <div className={classes.item}>
                    <FormControlLabel
                      className={classes.radioitem}
                      value={TIME_ENUM.SET_TIME}
                      control={<Radio />}
                      label={
                        <span className={classes.title}>
                          {t('tagCondition.setTime')}
                        </span>
                      }
                    />

                    <DatePicker
                      className={classes.datepicker}
                      selectedDate={startDate}
                      placeholder={t('datepicker.selectDate')}
                      onChange={value => {
                        setStartDate(value);
                        handleChangeStartDate(value);
                      }}
                      isHideDropdownIcon
                    />
                    <span>~</span>
                    <DatePicker
                      className={classes.datepicker}
                      selectedDate={endDate}
                      placeholder={t('datepicker.selectDate')}
                      onChange={value => {
                        setEndDate(value);
                        handleChangeEndDate(value);
                      }}
                      isHideDropdownIcon
                    />
                  </div>
                </RadioGroup>
              </div>
              <hr style={{ margin: 0 }} />
            </>
          )}
          <div className={classes.radioContainer}>
            <RadioGroup
              value={type}
              onChange={e => {
                setType(e.target.value);
                handleChangeType(e.target.value);
              }}
            >
              <div className={classes.item}>
                <FormControlLabel
                  className={classes.radioitem}
                  value={TYPE_ENUM.INCLUDE}
                  control={<Radio />}
                  label={
                    <span className={classes.title}>
                      {t('tagCondition.include')}
                    </span>
                  }
                />
                <AutoCompletedTextField
                  className={classes.tagInput}
                  placeholder={t(`tagCondition.autoCompleted-placeholder`)}
                  candidates={tagCandidates || []}
                  defaultTags={includeTags}
                  type="inline"
                  onSetTags={tagNames => {
                    setIncludeTags(tagNames);
                    handleChangeIncludeTags(tagNames);
                  }}
                  onChange={dbsHandleTagChange}
                  disabled={disabled}
                />
                <span className={classes.title}>
                  {t('tagCondition.andTagingCount')}
                </span>
                <TextField
                  type="number"
                  value={includeCount}
                  onChange={e => {
                    setIncludeCount(e.target.value);
                    handleChangeIncludeCount(e.target.value);
                  }}
                  className={classes.numInput}
                />
                <span className={classes.title}>{t('tagCondition.count')}</span>
              </div>
              <div className={classes.item}>
                <FormControlLabel
                  className={classes.radioitem}
                  value={TYPE_ENUM.EXCLUDE}
                  control={<Radio />}
                  label={
                    <span className={classes.title}>
                      {t('tagCondition.exclude')}
                    </span>
                  }
                />
                <AutoCompletedTextField
                  className={classes.tagInput}
                  placeholder={t(`tagCondition.autoCompleted-placeholder`)}
                  candidates={tagCandidates || []}
                  defaultTags={excludeTags}
                  type="inline"
                  onSetTags={tagNames => {
                    setExcludeTags(tagNames);
                    handleChangeExcludeTags(tagNames);
                  }}
                  onChange={dbsHandleTagChange}
                  disabled={disabled}
                />
              </div>
              <div className={classes.item}>
                <FormControlLabel
                  className={classes.radioitem}
                  value={TYPE_ENUM.ANY_GREATER}
                  control={<Radio />}
                  label={
                    <span className={classes.title}>
                      {t('tagCondition.someTagCount')}
                    </span>
                  }
                />
                <TextField
                  type="number"
                  value={someCount}
                  onChange={e => {
                    setSomeCount(e.target.value);
                    handleChangeSomeCount(e.target.value);
                  }}
                  className={classes.numInput}
                />
                <span className={classes.title}>{t('tagCondition.count')}</span>
              </div>
              <div className={classes.item}>
                <FormControlLabel
                  className={classes.radioitem}
                  value={TYPE_ENUM.NONE}
                  control={<Radio />}
                  label={
                    <span className={classes.title}>
                      {t('tagCondition.noTag')}
                    </span>
                  }
                />
              </div>
            </RadioGroup>
            <div className={classes.finishBlock}>
              <Button onClick={() => setOpen(false)} topic="primaryBlue">
                {t('tagCondition.finish')}
              </Button>
            </div>
          </div>
        </div>
      </ClickAwayListener>
    );
  }

  return (
    <Dialog
      open
      classes={{
        root: classes.root,
        paper: classes.paper,
      }}
    >
      <DialogTitle className={classes.dialogTitle}>
        {t('tagCondition.tagCondition')}
      </DialogTitle>
      <DialogContent className={classes.content}>{getContent()}</DialogContent>
    </Dialog>
  );
};

export default TagConditionPopup;
